import {
  INSTALLMENT_COUNT_TO_PREMIUM_PERIODICITY,
} from '@/features/proposals/components/capital-global/utils/capital-global-installment-periodicity.util';
import {
  LEGACY_GENERIC_DEBIT_PAYMENT_CODE,
  requiresBankAccountForPayment,
} from '@/features/proposals/hooks/capital-global/use-capital-global-proposal-payment-types.hook';
import type { CapitalGlobalProposalStepPagamentoValues } from '@/features/proposals/schemas/capital-global/proposal-step-pagamento.schema';
import type { PaymentType } from '@/services';
import type { PaymentInfo, Proposal } from '@/services/proposals/types';

const INSTALLMENT_COUNT_TO_PREMIUM_PERIODICITY_MAP =
  INSTALLMENT_COUNT_TO_PREMIUM_PERIODICITY;

function toInstallmentDateOnly(isoDate: string): string {
  if (!isoDate) return '';
  const s = String(isoDate);
  return s.split('T')[0]?.split(' ')[0] ?? '';
}

function dayOfMonthFromInstallmentDate(isoDate: string): number | null {
  const d = toInstallmentDateOnly(isoDate);
  const day = Number(d.split('-')[2]);
  return Number.isFinite(day) ? day : null;
}

function isInsureMoInstallmentOptionCatalog(blocks: PaymentInfo[]): boolean {
  if (!blocks || blocks.length < 2) return false;
  const methods = blocks
    .map(b => String(b.paymentMethod ?? '').trim())
    .filter(Boolean);
  if (methods.length < blocks.length) return false;
  return new Set(methods).size === 1;
}

function selectCatalogBlockMatchingPolicyInstallPeriod(
  blocks: PaymentInfo[],
  proposal: Proposal
): PaymentInfo | null {
  const raw = proposal.installPeriod;
  if (raw === undefined || raw === null) return null;
  const target = Number(raw);
  if (!Number.isFinite(target)) return null;

  const byCode = blocks.find(block => Number(block.installPeriod) === target);
  if (byCode) return byCode;

  return (
    blocks.find(block => Number(block.installmentPeriodCount) === target) ?? null
  );
}

function parseBankNumberWithDigit(
  value: string | number | undefined,
  maxNumberLength: number,
  maxDigitLength: number
) {
  let number = '';
  let digit = '';
  const raw = value == null ? '' : String(value).trim();

  if (raw) {
    const dash = raw.indexOf('-');
    if (dash >= 0) {
      number = raw.slice(0, dash).replace(/\D/g, '');
      digit = raw
        .slice(dash + 1)
        .toUpperCase()
        .replace(/[^0-9A-Z]/g, '');
    } else {
      number = raw.replace(/\D/g, '');
    }
  }

  return {
    number: number.slice(0, maxNumberLength),
    digit: digit.slice(0, maxDigitLength),
  };
}

function resolveEffectivePaymentBlock(proposal: Proposal): PaymentInfo | null {
  const blocks = proposal.paymentInfo;
  if (!blocks?.length) return null;

  if (isInsureMoInstallmentOptionCatalog(blocks)) {
    return selectCatalogBlockMatchingPolicyInstallPeriod(blocks, proposal);
  }

  const firstBlock = blocks[0];
  if (!firstBlock.installmentList?.length) return null;
  return firstBlock;
}

function resolveInstallmentPeriodCount(block: PaymentInfo): number {
  const fromField = block.installmentPeriodCount;
  if (fromField > 0) return fromField;
  return block.installmentList?.length ?? 0;
}

function resolveBestDueDay(block: PaymentInfo): string {
  const days = (block.installmentList ?? [])
    .map(item => dayOfMonthFromInstallmentDate(item.installmentDate))
    .filter((day): day is number => day !== null);

  if (!days.length) return '';

  const counts = new Map<number, number>();
  for (const day of days) {
    counts.set(day, (counts.get(day) ?? 0) + 1);
  }

  let modal = days[0];
  let modalCount = 0;
  for (const [day, count] of counts) {
    if (count > modalCount) {
      modal = day;
      modalCount = count;
    }
  }

  return String(modal);
}

function resolveBankFields(block: PaymentInfo): Pick<
  CapitalGlobalProposalStepPagamentoValues,
  'agency' | 'agencyDigit' | 'account' | 'accountDigit'
> {
  const agencyParts = parseBankNumberWithDigit(block.agencyNo, 5, 1);
  const accountParts = parseBankNumberWithDigit(block.accountNo, 13, 2);

  const legacyAgencyParts = !agencyParts.number
    ? parseBankNumberWithDigit(block.accountHolderIdNo, 5, 1)
    : null;
  const legacyAgencyDigit =
    legacyAgencyParts && block.accountHolderIdType
      ? String(block.accountHolderIdType)
          .toUpperCase()
          .replace(/[^0-9A-Z]/g, '')
          .slice(0, 1)
      : legacyAgencyParts?.digit;

  return {
    agency: agencyParts.number || legacyAgencyParts?.number || '',
    agencyDigit: agencyParts.digit || legacyAgencyDigit || '',
    account: accountParts.number,
    accountDigit: accountParts.digit,
  };
}

/**
 * Valores iniciais do Step 2 (Pagamento) a partir da proposta já persistida.
 * Retorna null quando não há paymentInfo utilizável (fluxo novo permanece com defaults).
 */
export function mapProposalToCapitalGlobalStepPagamentoPrefill(
  proposal: Proposal,
  paymentTypes: PaymentType[] = []
): Partial<CapitalGlobalProposalStepPagamentoValues> | null {
  const block = resolveEffectivePaymentBlock(proposal);
  if (!block) return null;

  const installmentCount = resolveInstallmentPeriodCount(block);
  const premiumPeriodicity =
    INSTALLMENT_COUNT_TO_PREMIUM_PERIODICITY_MAP[installmentCount];
  const paymentMethodCode = String(block.paymentMethod ?? '').trim();
  const bestDueDay = resolveBestDueDay(block);

  const result: Partial<CapitalGlobalProposalStepPagamentoValues> = {};

  if (premiumPeriodicity) {
    result.premiumPeriodicity = premiumPeriodicity;
  }
  if (paymentMethodCode) {
    result.paymentMethod = paymentMethodCode;
    const requiresBank =
      requiresBankAccountForPayment(paymentMethodCode, paymentTypes) ||
      (paymentMethodCode === LEGACY_GENERIC_DEBIT_PAYMENT_CODE &&
        Boolean(block.agencyNo || block.accountNo || block.accountHolderIdNo));
    if (requiresBank) {
      Object.assign(result, resolveBankFields(block));
    }
  }
  if (bestDueDay) {
    result.bestDueDay = bestDueDay;
  }

  if (
    !result.premiumPeriodicity &&
    !result.paymentMethod &&
    !result.bestDueDay
  ) {
    return null;
  }

  return result;
}
